/**
 * Student selection for the report wizard — Document 6 §9.11 step 1.
 *
 * Search by name, pick one. The same Turkish-aware folding as the student
 * list, so "ayse" finds "Ayşe".
 */

import { useMemo, useState } from 'react';
import { formatDateShort, formatValue } from '@pp/shared';
import type { StudentListResponse } from '../api/types.js';
import { useApiData } from '../hooks/useApiData.js';
import { useDebounced } from '../hooks/useDebounced.js';
import './StudentPicker.css';

interface StudentPickerProps {
  selectedId: string;
  onSelect: (id: string, name: string) => void;
}

export function StudentPicker({ selectedId, onSelect }: StudentPickerProps) {
  const [search, setSearch] = useState('');
  const debounced = useDebounced(search, 300);

  const path = useMemo(() => {
    const params = new URLSearchParams({ status: 'active' });
    if (debounced.trim()) params.set('search', debounced.trim());
    return `/api/students?${params.toString()}`;
  }, [debounced]);

  const { data, error, loading } = useApiData<StudentListResponse>(path);
  const items = data?.items ?? [];

  return (
    <div className="picker2">
      <h2 className="wiz__title">Öğrenci Seç</h2>

      <input
        type="search"
        className="picker2__search"
        placeholder="Öğrenci ara"
        aria-label="Öğrenci ara"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />

      {error && <p role="alert">{error}</p>}
      {loading && <p className="wiz__hint">Yükleniyor…</p>}

      {!loading && items.length === 0 && (
        <p className="wiz__empty">
          {debounced.trim()
            ? 'Aramanıza uygun öğrenci bulunamadı.'
            : 'Henüz öğrenci eklenmemiş.'}
        </p>
      )}

      <ul className="picker2__list">
        {items.map((student) => {
          const name = `${student.firstName} ${student.lastName}`;
          return (
            <li key={student.id}>
              <button
                type="button"
                className={
                  selectedId === student.id
                    ? 'picker2__item picker2__item--on'
                    : 'picker2__item'
                }
                onClick={() => onSelect(student.id, name)}
                aria-pressed={selectedId === student.id}
              >
                <span className="picker2__name">{name}</span>
                <span className="picker2__meta">
                  {student.age !== null && `${student.age} yaş · `}
                  {formatValue(student.currentWeightKg, 'kg', 1)}
                  {student.lastMeasurementDate
                    ? ` · Son ölçüm ${formatDateShort(student.lastMeasurementDate)}`
                    : ' · Henüz ölçüm yok'}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
